// Considered: PerspectivePickerMolecule and MultiSelectPerspectiveSelector — rejected because the
// first picks a single perspective *system name* (a catalogue), and the second multi-selects
// *assignees* (people) for one already-chosen perspective. Neither multi-selects perspective
// system names themselves with an Unattributed ("applies to everyone") option and grouped search.
// See spec Reuse Audit (arc-editor-canvas-v1.md).
import React, { useMemo, useRef } from 'react';
import { useTranslation } from 'localization';
import PickerShellMolecule, { PickerShellBucket } from '../PickerShell/PickerShellMolecule';
import OverlayPanelAtom, { OverlayPanelHandle } from '../../atoms/OverlayPanel/OverlayPanelAtom';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import ChipAtom from '../../atoms/Chip/ChipAtom';
import GlyphAtom from '../../atoms/Glyph/GlyphAtom';
import type { PerspectiveCatalogGroup } from '../../../utils/filterPropertyBuilder';
import { UNATTRIBUTED_LANE_KEY } from '../../../utils/arc/arcCanvasModel';
import './PerspectiveMultiPickerMolecule.scss';

/**
 * Sentinel in `value` meaning "named nobody — applies to everyone"
 * (arc-inspector.jsx's `AC_ANY_ROW`/`"any"`). Mutually exclusive with every real
 * perspective — picking it clears every other selection and vice versa. The same token the
 * lane grid's Unattributed row uses as its lane key, so a picker value and a lane key never
 * disagree about how "unattributed" is spelled.
 */
export const PERSPECTIVE_MULTI_PICKER_UNATTRIBUTED = UNATTRIBUTED_LANE_KEY;

export interface PerspectiveMultiPickerMoleculeProps {
  /** Selected perspective system names, or `[PERSPECTIVE_MULTI_PICKER_UNATTRIBUTED]`. */
  value: string[];
  /** The grouped catalog to choose from — see `buildPerspectiveCatalog`. */
  groups: PerspectiveCatalogGroup[];
  onChange: (value: string[]) => void;
  disabled?: boolean;
  /** True while the perspective catalog is still being resolved. */
  loading?: boolean;
  /** Non-null when the perspective catalog failed to resolve. */
  error?: string | null;
  testIdPrefix?: string;
  className?: string;
}

interface FlatPerspective {
  systemName: string;
  label: string;
  icon?: string;
  groupId: string;
  groupLabel: string;
  groupNote?: string;
}

const PerspectiveMultiPickerMolecule: React.FC<PerspectiveMultiPickerMoleculeProps> = ({
  value,
  groups,
  onChange,
  disabled = false,
  loading = false,
  error = null,
  testIdPrefix = 'perspective-multi-picker-option',
  className = '',
}) => {
  const { t } = useTranslation();
  const overlayRef = useRef<OverlayPanelHandle>(null);

  const isUnattributed = value.includes(PERSPECTIVE_MULTI_PICKER_UNATTRIBUTED);

  // The Unattributed row is a pinned pseudo-perspective, not a catalog entry — it lives in
  // its own group so it sorts first and reads as a distinct choice, not a member of Common.
  const flatItems = useMemo<FlatPerspective[]>(() => {
    const unattributed: FlatPerspective = {
      systemName: PERSPECTIVE_MULTI_PICKER_UNATTRIBUTED,
      label: t('studio.arcEditor.perspectiveMultiPicker.unattributed'),
      groupId: 'unattributed',
      groupLabel: t('studio.arcEditor.perspectiveMultiPicker.unattributedGroupLabel'),
      groupNote: t('studio.arcEditor.perspectiveMultiPicker.unattributedNote'),
    };
    const catalog = groups.flatMap((group) =>
      group.perspectives.map((p) => ({
        systemName: p.systemName,
        label: p.label,
        icon: p.icon,
        groupId: group.id,
        groupLabel: group.label,
        groupNote: group.note,
      }))
    );
    return [unattributed, ...catalog];
  }, [groups, t]);

  const labelFor = (systemName: string) =>
    flatItems.find((p) => p.systemName === systemName)?.label ?? systemName;

  const toggle = (systemName: string) => {
    if (systemName === PERSPECTIVE_MULTI_PICKER_UNATTRIBUTED) {
      onChange(isUnattributed ? [] : [PERSPECTIVE_MULTI_PICKER_UNATTRIBUTED]);
      return;
    }
    const next = value.includes(systemName)
      ? value.filter((v) => v !== systemName)
      : value.filter((v) => v !== PERSPECTIVE_MULTI_PICKER_UNATTRIBUTED).concat(systemName);
    onChange(next);
  };

  const computeBuckets = useMemo(
    () =>
      (filtered: FlatPerspective[]): PickerShellBucket<FlatPerspective>[] => {
        const order = ['unattributed', ...groups.map((g) => g.id)];
        const byGroup = new Map<string, FlatPerspective[]>();
        filtered.forEach((item) => {
          const bucket = byGroup.get(item.groupId) ?? [];
          bucket.push(item);
          byGroup.set(item.groupId, bucket);
        });
        return order
          .filter((id) => byGroup.has(id))
          .map((id) => {
            const items = byGroup.get(id) ?? [];
            return {
              key: id,
              label: items[0]?.groupLabel ?? id,
              note: items[0]?.groupNote,
              items,
            };
          });
      },
    [groups]
  );

  return (
    <div className={`perspective-multi-picker-molecule ${className}`.trim()}>
      <div
        className="perspective-multi-picker-molecule__field"
        data-empty={value.length === 0 ? 'true' : undefined}
        data-datum="side.perspective-multi-picker"
        data-kind="field:multi-select"
        data-container="each"
      >
        {value.length === 0 && (
          <span
            className="perspective-multi-picker-molecule__placeholder"
            data-datum="side.nobody-named-applies-to-everyone"
            data-kind="hint"
          >
            {t('studio.arcEditor.perspectiveMultiPicker.placeholder')}
          </span>
        )}
        {value.map((systemName, index) => (
          <ChipAtom
            key={systemName}
            label={labelFor(systemName)}
            icon={systemName === PERSPECTIVE_MULTI_PICKER_UNATTRIBUTED ? 'lucide:circle-slash' : undefined}
            removable={!disabled}
            removeAriaLabel={t('studio.arcEditor.perspectiveMultiPicker.removeAria', {
              name: labelFor(systemName),
            })}
            onRemove={() => toggle(systemName)}
            data-datum={index === 0 ? 'side.p-name' : undefined}
            data-kind={index === 0 ? 'chip' : undefined}
            removeDatum={index === 0 ? 'side.button#5' : undefined}
          />
        ))}
        {!disabled && (
          <ButtonAtom
            variant="text"
            size="small"
            icon={<GlyphAtom glyph="lucide:plus" aria-hidden />}
            label={t('studio.arcEditor.perspectiveMultiPicker.addPerspective')}
            onClick={(e) => overlayRef.current?.toggle(e)}
            data-datum="side.name-a-perspective"
            data-kind="button"
          />
        )}
      </div>
      <OverlayPanelAtom
        ref={overlayRef}
        dismissable
        className="perspective-multi-picker-molecule__overlay"
        data-datum="side.reactdom-createportal-div-classname-ac-p"
        data-kind="picker"
        data-container="side"
      >
        <PickerShellMolecule<FlatPerspective>
          items={flatItems}
          getSearchText={(p) => `${p.label} ${p.systemName}`}
          getRowKey={(p) => p.systemName}
          renderRow={(p) => (
            <div
              className="perspective-multi-picker-molecule__row"
              data-testid={`${testIdPrefix}-${p.systemName}`}
            >
              {p.icon && <i className={p.icon} aria-hidden="true" />}
              <span className="perspective-multi-picker-molecule__row-label">{p.label}</span>
              {value.includes(p.systemName) && (
                <i className="pi pi-check perspective-multi-picker-molecule__row-check" aria-hidden="true" />
              )}
            </div>
          )}
          onPickItem={(p) => toggle(p.systemName)}
          isItemSelected={(p) => value.includes(p.systemName)}
          computeBuckets={computeBuckets}
          loading={loading}
          loadingMessage={t('studio.arcEditor.perspectiveMultiPicker.loading')}
          errorMessage={error}
          noMatchesMessage={t('studio.arcEditor.perspectiveMultiPicker.noMatches')}
          searchPlaceholder={t('studio.arcEditor.perspectiveMultiPicker.searchPlaceholder')}
          testIdPrefix={testIdPrefix}
        />
      </OverlayPanelAtom>
    </div>
  );
};

PerspectiveMultiPickerMolecule.displayName = 'PerspectiveMultiPickerMolecule';

export default PerspectiveMultiPickerMolecule;
